import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, LayoutDashboard, Trophy, Settings, Search, LifeBuoy } from "lucide-react";

export default function Header() {
  const { user, logout, can, hasAdminAccess } = useAuth();
  const [s, setS] = useState(() => (typeof window !== "undefined" && window.__siteSettings) || {});
  const navigate = useNavigate();
  const location = useLocation();

  const refresh = async () => {
    try { const { data } = await api.get("/settings"); setS(data); } catch {}
  };
  useEffect(() => { refresh(); }, [location.pathname]);
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("settings-updated", h);
    return () => window.removeEventListener("settings-updated", h);
  }, []);

  const title = s.site_title || "Sports Record Book";
  const schoolName = s.school_name || "";
  const tagline = s.tagline || "";
  

  return (
    <>
      {s.announcement_active && s.announcement && (
        <div className="w-full text-center py-1.5 px-3 text-xs md:text-sm font-medium"
             style={{ background: "var(--brand-accent)", color: "var(--brand-primary)" }}
             data-testid="announcement-banner">
          {s.announcement}
        </div>
      )}
      <header className="text-white border-b-4" style={{ background: "var(--brand-primary)", borderColor: "var(--brand-accent)" }} data-testid="app-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
          <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="header-home-link">
            {s.logo_url ? (
              <img src={s.logo_url} alt="Logo" className="h-10 w-10 object-contain bg-white p-0.5" data-testid="site-logo"/>
            ) : (
              <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: "var(--brand-accent)" }}>
                <Trophy className="w-5 h-5" style={{ color: "var(--brand-primary)" }} strokeWidth={2.5}/>
              </div>
            )}
            <div className="min-w-0">
              <div className="font-overline text-[10px] truncate" style={{ color: "var(--brand-accent)" }}>
                {schoolName || "Est. Records"}
              </div>
              <h1 className="font-heading text-xl md:text-2xl leading-none tracking-tight truncate" data-testid="site-title">{title}</h1>
              {tagline && <div className="text-[10px] text-white/70 truncate mt-0.5">{tagline}</div>}
            </div>
          </Link>
          <nav className="flex items-center gap-1 overflow-x-auto max-w-full -mx-2 px-2 md:mx-0 md:px-0" data-testid="main-nav">
            <Link to="/" data-testid="nav-home">
              <Button variant="ghost" className="text-white hover:bg-white hover:text-[color:var(--brand-primary)] rounded-sm font-heading tracking-wide">Sports</Button>
            </Link>
            <Link to="/search" data-testid="nav-search">
              <Button variant="ghost" className="text-white hover:bg-white hover:text-[color:var(--brand-primary)] rounded-sm font-heading tracking-wide">
                <Search className="w-4 h-4 mr-2"/>Search
              </Button>
            </Link>
            <Link to="/help" data-testid="nav-help">
              <Button variant="ghost" className="text-white hover:bg-white hover:text-[color:var(--brand-primary)] rounded-sm font-heading tracking-wide">
                <LifeBuoy className="w-4 h-4 mr-2"/>Help
              </Button>
            </Link>
            {hasAdminAccess && (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard">
                  <Button variant="ghost" className="text-white hover:bg-white hover:text-[color:var(--brand-primary)] rounded-sm font-heading tracking-wide">
                    <LayoutDashboard className="w-4 h-4 mr-2"/>Dashboard
                  </Button>
                </Link>
                {(can("manage_sports") || can("reorder_records")) && <Link to="/admin/sports" data-testid="nav-admin-sports">
                  <Button variant="ghost" className="text-white hover:bg-white hover:text-[color:var(--brand-primary)] rounded-sm font-heading tracking-wide">
                    {can("manage_sports") ? "Sport Admin" : "Sport Records"}
                  </Button>
                </Link>}
                {can("manage_site_settings") && (
                  <Link to="/admin/site" data-testid="nav-admin-site">
                    <Button variant="ghost" className="text-white hover:bg-white hover:text-[color:var(--brand-primary)] rounded-sm font-heading tracking-wide">
                      <Settings className="w-4 h-4 mr-1"/>Site
                    </Button>
                  </Link>
                )}
              </>
            )}
            {user ? (
              <Button onClick={async () => { await logout(); navigate("/"); }} data-testid="btn-logout"
                className="rounded-sm font-heading" style={{ background: "var(--brand-accent)", color: "var(--brand-primary)" }}>
                <LogOut className="w-4 h-4 mr-2"/>Logout
              </Button>
            ) : (
              <Link to="/login" data-testid="nav-login">
                <Button className="rounded-sm font-heading" style={{ background: "var(--brand-accent)", color: "var(--brand-primary)" }}>
                  <LogIn className="w-4 h-4 mr-2"/>Sign In
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </header>
    </>
  );
}
